import React, { useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import Layout from "./components/Layout";
import Login from "./components/Login";
import PrivateRoutes from "./components/utils/PrivateRoutes";
import VenueTypeRoute from "./components/utils/VenueTypeRoute";
import VenueActivationRoute from "./components/utils/VenueActivationRoute";
import SentryUserContext from "./components/utils/SentryUserContext";
import { VENUE_TYPES } from "./lib/consts/venueTypes";
import { UserContext } from "./context/UserContext";
import { getDefaultVenuePath } from "./lib/consts/navigation";

import Product from "./components/Sidebar/ProductsComponents/Products";
import Superusers from "./components/Sidebar/SuperuserComponents/Superusers";
import Shops from "./components/Sidebar/ShopsComponents/Shops";
import Genetics from "./components/Sidebar/GeneticsComponents/Genetics";
import Customers from "./components/Sidebar/CustomerComponents/Customers";
import BarraPage from "./components/Sidebar/BarraComponents/BarraPage";
import ControlByVenueType from "./components/Sidebar/ClubEntradasComponents/ControlByVenueType";
import MemberApp, { MemberLink, MemberTable } from "./components/MemberApp/MemberApp";
import Dispenser from "./components/Sidebar/DispenserComponents/Dispenser";
import StockBoard from "./components/Sidebar/StockBoardComponents/StockBoard";
import SmackPage from "./components/Smack/SmackPage";
import Transactions from "./components/Sidebar/FinanceComponents/Transactions";
import CajasPage from "./components/Sidebar/CajasComponents/CajasPage";
import PagosTercerosPage from "./components/Sidebar/PagosTercerosComponents/PagosTercerosPage";
import ReportsPage from "./components/Sidebar/ReportsComponents/ReportsPage";
import ActivityPage from "./components/Sidebar/ActivityComponents/ActivityPage";
import ProfitabilityPage from "./components/Sidebar/ProfitabilityComponents/ProfitabilityPage";
import ImpuestosPage from "./components/Sidebar/ImpuestosComponents/ImpuestosPage";
import Owners from "./components/Sidebar/OwnersComponents/Owners";
import Settings from "./components/Sidebar/Settings";
import Profile from "./components/Sidebar/ProfileComponents/Profile";
import Support from "./components/Sidebar/SupportComponents/Support";
import AsistenteIAPage from "./components/AI/AsistenteIAPage";
import Carritos from "./components/Sidebar/CarritosComponents/Carritos";
import CalendarPage from "./components/Sidebar/CalendarComponents/CalendarPage";
import FacturacionPage from "./components/Sidebar/FacturacionComponents/FacturacionPage";
import PreInscripcionesPage from "./components/Sidebar/PreInscripcionesComponents/PreInscripcionesPage";
import { PreRegisterRoute } from "./components/PreRegister/PreRegisterErrorBoundary";
import VerifyEmail from "./components/VerifyEmail";
import ResetPassword from "./components/ResetPassword";
import Landing from "./components/Landing/Landing";
import OpsPage from "./components/Sidebar/OpsComponents/OpsPage";
import CookieConsent from "./components/Landing/CookieConsent";
import AvisoLegal from "./components/Legal/AvisoLegal";
import PoliticaPrivacidad from "./components/Legal/PoliticaPrivacidad";
import PoliticaCookies from "./components/Legal/PoliticaCookies";
import Terminos from "./components/Legal/Terminos";
import SociosPage from "./components/Sidebar/SociosComponents/SociosPage";
import { SociosDevHarness, PreinscripcionDevHarness } from "./components/dev/SociosDevHarness";
import { DEMO_DATA as SOCIOS_DEMO } from "./components/dev/sociosDemoData";
import DevShellPreview from "./components/dev/DevShellPreview";
import DispensarioPage from "./components/Sidebar/DispensarioComponents/DispensarioPage";
import DispensarioDevHarness from "./components/dev/DispensarioDevHarness";
import ImpresoraDevHarness from "./components/dev/ImpresoraDevHarness";
import { DEMO_DATA as DISPENSARIO_DEMO } from "./components/dev/dispensarioDemoData";
import { DEV_ROUTES_ENABLED } from "./lib/devRoutes";

const DefaultVenueLanding = () => {
  const [, , , , , , , , , , , venueType] = useContext(UserContext);
  return <Navigate to={getDefaultVenuePath(venueType)} replace />;
};



const App = () => (
  <Router
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
    <SentryUserContext />
    <CookieConsent />
    <Routes>
      {/* ── Landing Pública ── */}
      <Route path="/" element={<Landing />} />
      {/* PWA del socio (CRUO): canal propio, sin sidebar de staff */}
      <Route path="/app" element={<MemberApp />} />
      <Route path="/app/vincular" element={<MemberLink />} />
      <Route path="/mesa/:qrToken" element={<MemberTable />} />

      {/* Diagnóstico de impresoras Bluetooth: herramienta de soporte, sin datos del club.
          Fuera del gate de abajo A PROPÓSITO: se usa en el club (Web Bluetooth exige
          HTTPS) con una impresora muerta y soporte al teléfono; ver su cabecera. */}
      <Route path="/dev/impresora" element={<ImpresoraDevHarness />} />

      {/* ── /dev/*: maquetas y previews sin sesión. Solo fuera de producción
          (o con REACT_APP_DEV_ROUTES=1 en el build): ver src/lib/devRoutes.js.
          Toda ruta /dev/* nueva va DENTRO de este bloque (candado: devRoutes.test.js). ── */}
      {DEV_ROUTES_ENABLED && (
        <>
          {/* Socios redesign — previews (Figma design-to-code, mock data) */}
          <Route path="/dev/socios" element={<SociosDevHarness />} />
          <Route path="/dev/preinscripcion" element={<PreinscripcionDevHarness />} />
          {/* Dispensario redesign — previews (Figma design-to-code, mock data) */}
          <Route path="/dev/dispensario" element={<DispensarioDevHarness />} />

          {/* Previews dentro del shell real (UserContext simulado → sidebar completo) */}
          <Route element={<DevShellPreview />}>
            <Route element={<Layout />}>
              <Route path="/dev/socios-shell" element={<SociosPage demo={SOCIOS_DEMO} />} />
              <Route path="/dev/dispensario-shell" element={<DispensarioPage demo={DISPENSARIO_DEMO} />} />
            </Route>
          </Route>
        </>
      )}

      {/* app shell: sidebar + content */}
      <Route element={<Layout />}>
        {/* ── both superadmin & owner & staff ── */}
        <Route element={<PrivateRoutes allowedRoles={["superadmin", "owner", "staff"]} />}>
          <Route path="dashboard" element={<VenueActivationRoute><DefaultVenueLanding /></VenueActivationRoute>} />
          <Route
            path="control"
            element={
              <VenueActivationRoute>
                <VenueTypeRoute allowedVenueTypes={[VENUE_TYPES.CANNABIS_CLUB, VENUE_TYPES.SOCIAL_CLUB]}>
                  <ControlByVenueType />
                </VenueTypeRoute>
              </VenueActivationRoute>
            }
          />
          <Route
            path="barra"
            element={
              <VenueActivationRoute>
                <VenueTypeRoute allowedVenueTypes={[VENUE_TYPES.SOCIAL_CLUB, VENUE_TYPES.BISTRO]}>
                  <BarraPage />
                </VenueTypeRoute>
              </VenueActivationRoute>
            }
          />
          <Route
            path="dispenser"
            element={
              <VenueActivationRoute>
                <VenueTypeRoute allowedVenueTypes={[VENUE_TYPES.CANNABIS_CLUB, VENUE_TYPES.CBD_SHOP]}>
                  <Dispenser />
                </VenueTypeRoute>
              </VenueActivationRoute>
            }
          />
          {/* Rediseño de Figma. Corre en paralelo al /dispenser de producción
              mientras se valida; al sustituirlo, esta ruta se queda con el nombre. */}
          <Route
            path="dispensario"
            element={
              <VenueActivationRoute>
                <VenueTypeRoute allowedVenueTypes={[VENUE_TYPES.CANNABIS_CLUB, VENUE_TYPES.CBD_SHOP]}>
                  <DispensarioPage />
                </VenueTypeRoute>
              </VenueActivationRoute>
            }
          />
          <Route
            path="carta"
            element={
              <VenueActivationRoute>
                <VenueTypeRoute allowedVenueTypes={[VENUE_TYPES.BISTRO]}>
                  <Product />
                </VenueTypeRoute>
              </VenueActivationRoute>
            }
          />
          <Route path="almacen" element={<VenueActivationRoute><Product /></VenueActivationRoute>} />
          <Route path="ubicaciones" element={<VenueActivationRoute><StockBoard /></VenueActivationRoute>} />
          <Route
            path="customers"
            element={
              <VenueActivationRoute>
                <VenueTypeRoute allowedVenueTypes={[VENUE_TYPES.CANNABIS_CLUB, VENUE_TYPES.SOCIAL_CLUB]}>
                  <Customers />
                </VenueTypeRoute>
              </VenueActivationRoute>
            }
          />
          <Route path="cajas" element={<VenueActivationRoute><CajasPage /></VenueActivationRoute>} />
          <Route path="pagos-terceros" element={<VenueActivationRoute><PagosTercerosPage /></VenueActivationRoute>} />
          <Route path="estadisticas" element={<VenueActivationRoute><ReportsPage /></VenueActivationRoute>} />
          <Route path="rentabilidad" element={<VenueActivationRoute><ProfitabilityPage /></VenueActivationRoute>} />
          <Route path="impuestos" element={<VenueActivationRoute><ImpuestosPage /></VenueActivationRoute>} />
          <Route path="transactions" element={<VenueActivationRoute><Transactions /></VenueActivationRoute>} />
          <Route path="actividad" element={<VenueActivationRoute><ActivityPage /></VenueActivationRoute>} />
          {/* Smack (chat equipo+club). Sustituye a la vieja sección "Mensajes"
              (maquetón sin backend, borrado): misma zona del router, módulo real. */}
          <Route path="smack" element={<VenueActivationRoute><SmackPage /></VenueActivationRoute>} />
          <Route path="staff" element={<VenueActivationRoute><Owners /></VenueActivationRoute>} />
          <Route path="owners" element={<VenueActivationRoute><Owners /></VenueActivationRoute>} />
          <Route path="genetics" element={<VenueActivationRoute><Genetics /></VenueActivationRoute>} />
          <Route path="preinscripciones" element={<VenueActivationRoute><PreInscripcionesPage /></VenueActivationRoute>} />
          <Route path="settings" element={<Settings />} />
          <Route path="profile" element={<Profile />} />
          <Route path="support" element={<Support />} />
          <Route path="asistente-ia" element={<VenueActivationRoute><AsistenteIAPage /></VenueActivationRoute>} />
          <Route path="workspace" element={<DefaultVenueLanding />} />
          <Route path="carritos" element={<VenueActivationRoute><Carritos /></VenueActivationRoute>} />
          <Route path="calendar" element={<VenueActivationRoute><CalendarPage /></VenueActivationRoute>} />
        </Route>



        {/* ── superadmin only ── */}
        <Route element={<PrivateRoutes allowedRoles={["superadmin"]} />}>
          <Route path="superusers" element={<Superusers />} />
          <Route path="shops" element={<Shops />} />
          <Route path="ops" element={<OpsPage />} />
        </Route>

        {/* ── owner only ── */}
        <Route element={<PrivateRoutes allowedRoles={["owner", "superadmin"]} />}>
          <Route path="my_shops" element={<Shops />} />
          <Route path="facturacion" element={<FacturacionPage />} />
        </Route>

        {/* inside‐layout 404 */}
        <Route path="*" element={<div>Page not found</div>} />
      </Route>

      <Route path="pre-register/:venueToken" element={<PreRegisterRoute />} />
      <Route path="login" element={<Login />} />
      <Route path="verify-email" element={<VerifyEmail />} />
      <Route path="reset-password" element={<ResetPassword />} />

      {/* ── Páginas legales (públicas) ── */}
      <Route path="aviso-legal" element={<AvisoLegal />} />
      <Route path="privacidad" element={<PoliticaPrivacidad />} />
      <Route path="cookies" element={<PoliticaCookies />} />
      <Route path="terminos" element={<Terminos />} />

      {/* any completely unknown path → back to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Router>
);

export default App;
